import { useTranslation } from 'react-i18next'
import { useOnlineStatus, type SyncStatusType } from '../../hooks/useOnlineStatus'
import { WifiOffIcon, SyncIcon, CheckCircleIcon, ExclamationCircleIcon, CloudUploadIcon } from '../icons'

/** Props for StatusContent component */
interface StatusContentProps {
  syncStatus: SyncStatusType
  pendingCount: number
  conflictCount: number
  onSyncClick: () => void
  t: (key: string, options?: Record<string, unknown>) => string
}

/**
 * Renders the appropriate status content based on sync state
 */
function StatusContent({
  syncStatus,
  pendingCount,
  conflictCount,
  onSyncClick,
  t
}: StatusContentProps): JSX.Element {
  switch (syncStatus) {
    case 'syncing':
      return (
        <>
          <SyncIcon className="w-4 h-4 animate-spin" />
          <span>{t('sync.syncing')}</span>
        </>
      )

    case 'pending':
      return (
        <>
          <CloudUploadIcon className="w-4 h-4" />
          <span>{t('sync.changesPending', { count: pendingCount })}</span>
          <button
            onClick={onSyncClick}
            className="ml-2 px-2 py-0.5 bg-white/20 rounded text-xs hover:bg-white/30 transition-colors"
            type="button"
          >
            {t('sync.syncNow')}
          </button>
        </>
      )

    case 'error':
      return (
        <>
          <ExclamationCircleIcon className="w-4 h-4" />
          <span>{t('sync.syncFailed')}</span>
          <button
            onClick={onSyncClick}
            className="ml-2 px-2 py-0.5 bg-white/20 rounded text-xs hover:bg-white/30 transition-colors"
            type="button"
          >
            {t('common.retry')}
          </button>
        </>
      )

    case 'conflict':
      return (
        <>
          <ExclamationCircleIcon className="w-4 h-4" />
          <span>{t('sync.conflictsNeedAttention', { count: conflictCount })}</span>
        </>
      )

    default:
      return (
        <>
          <CheckCircleIcon className="w-4 h-4" />
          <span>{t('sync.allSynced')}</span>
        </>
      )
  }
}

/**
 * Get background color class based on status
 */
function getStatusBackgroundClass(isOnline: boolean, syncStatus: SyncStatusType): string {
  if (!isOnline) return 'bg-yellow-500'

  switch (syncStatus) {
    case 'syncing':
      return 'bg-blue-500'
    case 'pending':
      return 'bg-blue-500'
    case 'error':
      return 'bg-red-500'
    case 'conflict':
      return 'bg-orange-500'
    default:
      return 'bg-green-500'
  }
}

/**
 * Offline indicator banner with sync status
 * Shows when the user is offline, syncing, or has pending changes
 */
function OfflineIndicator(): JSX.Element | null {
  const { t } = useTranslation()
  const {
    isOnline,
    wasOffline,
    syncStatus,
    pendingCount,
    conflictCount,
    manualSync,
    isSyncing
  } = useOnlineStatus()

  // Determine if we should show the indicator
  const shouldShow =
    !isOnline ||
    wasOffline ||
    syncStatus === 'syncing' ||
    syncStatus === 'pending' ||
    syncStatus === 'error' ||
    syncStatus === 'conflict'

  if (!shouldShow) {
    return null
  }

  const handleSyncClick = (): void => {
    if (!isSyncing && isOnline) {
      void manualSync()
    }
  }

  const backgroundClass = getStatusBackgroundClass(isOnline, syncStatus)

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`fixed top-0 left-0 right-0 z-50 py-2 px-4 text-center text-sm font-medium transition-all text-white ${backgroundClass}`}
    >
      <div className="flex items-center justify-center gap-2">
        {!isOnline ? (
          <>
            <WifiOffIcon className="w-4 h-4" />
            <span>{t('sync.offlineMessage')}</span>
            {pendingCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                {t('sync.pending', { count: pendingCount })}
              </span>
            )}
          </>
        ) : wasOffline && syncStatus === 'idle' ? (
          <>
            <CheckCircleIcon className="w-4 h-4" />
            <span>{t('sync.backOnline')}</span>
          </>
        ) : (
          <StatusContent
            syncStatus={syncStatus}
            pendingCount={pendingCount}
            conflictCount={conflictCount}
            onSyncClick={handleSyncClick}
            t={t}
          />
        )}
      </div>
    </div>
  )
}

export default OfflineIndicator
